/**
 * SystemAccountsModal
 *
 * Lists all reserved account codes grouped by account type.
 * Accounts that already exist are shown with a checkmark.
 * Missing accounts can be created with one click — the form opens pre-filled
 * with the correct code, name, subtype, and parent so the accountant doesn't
 * have to know the internal convention.
 */

import { useMemo } from 'react'

import { Button, Modal } from '@elsapiens/ui'
import { CheckCircle2 } from 'lucide-react'

import { RESERVED_CODES, type ReservedAccountCode } from '../../constants/reserved-account-codes'
import { ACCOUNT_TYPE_LABELS, type AccountType } from '../../constants/labels'
import { type Account } from '../../services/account-service'

interface SystemAccountsModalProps {
  isOpen: boolean
  accountsMap: Map<string, Account>
  onClose: () => void
  onCreateReserved: (reserved: ReservedAccountCode) => void
  // Authoritative catalog from GET /reserved-accounts (carries system_tag + canonical
  // codes). Falls back to the static RESERVED_CODES if not yet loaded.
  reservedAccounts?: ReservedAccountCode[]
}

const TYPE_ORDER: AccountType[] = [
  'ACCOUNT_TYPE_ASSET',
  'ACCOUNT_TYPE_LIABILITY',
  'ACCOUNT_TYPE_EQUITY',
  'ACCOUNT_TYPE_REVENUE',
  'ACCOUNT_TYPE_EXPENSE',
]

interface ReservedEntry {
  reserved: ReservedAccountCode
  existingAccount: Account | undefined
  parentExists: boolean
}

function useReservedEntries(accountsMap: Map<string, Account>, catalog: ReservedAccountCode[]): Map<AccountType, ReservedEntry[]> {
  return useMemo(() => {
    const codeToAccount = new Map<string, Account>()
    accountsMap.forEach(a => codeToAccount.set(a.code, a))

    const grouped = new Map<AccountType, ReservedEntry[]>()
    for (const reserved of catalog) {
      const existing = codeToAccount.get(reserved.code)
      const parentExists = codeToAccount.has(reserved.parentCode)
      const entry: ReservedEntry = { reserved, existingAccount: existing, parentExists }
      const group = grouped.get(reserved.accountType) ?? []
      group.push(entry)
      grouped.set(reserved.accountType, group)
    }
    // Sort each group by code ascending
    grouped.forEach(group => group.sort((a, b) => a.reserved.code.localeCompare(b.reserved.code)))
    return grouped
  }, [accountsMap, catalog])
}

function StatusBadge({ entry, onCreateReserved }: { entry: ReservedEntry; onCreateReserved: (r: ReservedAccountCode) => void }): JSX.Element {
  if (entry.existingAccount) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Created
      </span>
    )
  }
  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={() => onCreateReserved(entry.reserved)}
    >
      Set Up
    </Button>
  )
}

function ReservedRow({ entry, onCreateReserved }: { entry: ReservedEntry; onCreateReserved: (r: ReservedAccountCode) => void }): JSX.Element {
  const { reserved, existingAccount, parentExists } = entry
  return (
    <div className={`flex items-center justify-between gap-3 py-2.5 border-b border-border/50 last:border-0 ${existingAccount ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="shrink-0 font-mono text-xs font-semibold px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">
          {reserved.code}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {existingAccount?.name ?? reserved.defaultName}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {reserved.purpose}
            {!existingAccount && !parentExists && (
              <span className="ml-1.5 text-amber-600 dark:text-amber-400">· parent account missing</span>
            )}
          </p>
        </div>
      </div>
      <div className="shrink-0">
        <StatusBadge entry={entry} onCreateReserved={onCreateReserved} />
      </div>
    </div>
  )
}

function TypeSection({ accountType, entries, onCreateReserved }: {
  accountType: AccountType
  entries: ReservedEntry[]
  onCreateReserved: (r: ReservedAccountCode) => void
}): JSX.Element {
  const created = entries.filter(e => e.existingAccount).length
  return (
    <div className="el-mb-md">
      <div className="flex items-center justify-between el-mb-xs">
        <h3 className="text-sm font-semibold text-foreground-secondary uppercase tracking-wide">
          {ACCOUNT_TYPE_LABELS[accountType]}
        </h3>
        <span className="text-xs text-muted-foreground">{created}/{entries.length} set up</span>
      </div>
      <div className="rounded-md border border-border bg-surface px-3">
        {entries.map(entry => (
          <ReservedRow key={entry.reserved.code} entry={entry} onCreateReserved={onCreateReserved} />
        ))}
      </div>
    </div>
  )
}

export function SystemAccountsModal({ isOpen, accountsMap, onClose, onCreateReserved, reservedAccounts }: SystemAccountsModalProps): JSX.Element {
  const catalog = useMemo(
    () => (reservedAccounts && reservedAccounts.length > 0 ? reservedAccounts : Object.values(RESERVED_CODES)),
    [reservedAccounts],
  )
  const grouped = useReservedEntries(accountsMap, catalog)

  function handleCreate(reserved: ReservedAccountCode): void {
    onClose()
    onCreateReserved(reserved)
  }

  const totalCreated = useMemo(() => {
    let n = 0
    grouped.forEach(entries => { n += entries.filter(e => e.existingAccount).length })
    return n
  }, [grouped])
  const totalCount = catalog.length

  return (
    <Modal isOpen={isOpen} size="lg" title="System Reserved Accounts" onClose={onClose}>
      <p className="text-sm text-muted-foreground el-mb-md">
        These accounts are used by built-in features like quick-entry, payroll, and tax flows.
        Click <strong>Set Up</strong> to create any missing one — the form opens pre-filled with
        the correct code, name, and subtype.
        <span className="ml-2 font-medium text-foreground">{totalCreated}/{totalCount} set up.</span>
      </p>

      <div className="max-h-[60vh] overflow-y-auto pr-1">
        {TYPE_ORDER.map(type => {
          const entries = grouped.get(type)
          if (!entries?.length) { return null }
          return (
            <TypeSection
              key={type}
              accountType={type}
              entries={entries}
              onCreateReserved={handleCreate}
            />
          )
        })}
      </div>

      <div className="flex justify-end el-mt-lg">
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </Modal>
  )
}
